import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Sparklines, SparklinesLine, SparklinesCurve } from 'react-sparklines';
interface KPICardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    label: string;
    data: number[];
  };
  /** 'standard' = green up / red down; 'pending' = red up / yellow down / green if 0 */
  trendColorMode?: 'standard' | 'pending';
  borderColor?: string;
}
const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  trendColorMode = 'standard',
  borderColor = '#3B82F6'
}) => {
  const getTrendColor = () => {
    if (!trend) return '#10B981';
    if (trendColorMode === 'pending') {
      return '#F59E0B'; // always yellow for pending
    }
    // standard: green up, red down
    return trend.value >= 0 ? '#10B981' : '#EF4444';
  };
  const trendColor = getTrendColor();
  return <Card sx={{
    p: 0,
    borderRadius: '14px',
    border: `1px solid ${borderColor}30`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    background: '#FFFFFF',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${borderColor}15, 0 1px 3px rgba(0,0,0,0.06)`,
      borderColor: `${borderColor}50`
    }
  }} data-id="element-1228" data-mp="x7v8zgdmuo6j">
      {/* Top accent line */}
      <Box sx={{
      height: 3,
      background: `linear-gradient(90deg, ${color}, ${color}80)`,
      borderRadius: '14px 14px 0 0'
    }} data-id="element-1229" data-mp="1oxpjjcvh1yps" />

      <Box sx={{
      p: 2,
      pt: 1.5
    }} data-id="element-1230" data-mp="1f8pm3xwmbbdp">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: trend ? 1 : 0
      }} data-id="element-1231" data-mp="22ytjhnj7qew9">
          <Box sx={{
          minWidth: 0,
          flex: 1
        }} data-id="element-1232" data-mp="2hlihfkxtgk0j">
            <Typography variant="body2" sx={{
            color: '#94A3B8',
            fontSize: '0.7rem',
            fontWeight: 600,
            mb: 0.3,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            lineHeight: 1.3,
            minHeight: '2.2em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }} data-id="element-1233" data-mp="2dk6trkyi68uz">
              {title}
            </Typography>
            <Typography variant="h5" sx={{
            fontWeight: 800,
            color: '#0F172A',
            fontSize: '1.35rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em'
          }} data-id="element-1234" data-mp="1kvhyi2k4oxr5">
              {value}
            </Typography>
          </Box>
          <Box sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${color}12, ${color}20)`,
          color: color,
          flexShrink: 0,
          ml: 1
        }} data-id="element-1235" data-mp="10sc1jimku085">
            {icon}
          </Box>
        </Box>

        {trend && <Box data-id="element-1236" data-mp="n0xijbso5z13">
            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5
        }} data-id="element-1237" data-mp="n2ib4vvfb7wb">
              <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.3,
            bgcolor: `${trendColor}10`,
            borderRadius: '6px',
            px: 0.8,
            py: 0.2
          }} data-id="element-1238" data-mp="wslcbjcp7s5q">
                <Typography variant="caption" sx={{
              color: trendColor,
              fontWeight: 700,
              fontSize: '0.65rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.2,
              lineHeight: 1.4
            }} data-id="element-1239" data-mp="260oco7veubwy">
                  {trend.value > 0 ? '▲' : trend.value < 0 ? '▼' : '—'}{' '}
                  {Math.abs(trend.value)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{
          height: 26,
          width: '100%',
          opacity: 0.85
        }} data-id="element-1240" data-mp="2yo0w4969y3m1">
              <Sparklines data={trend.data} width={120} height={26} margin={2} data-id="element-1241" data-mp="14b1a9ybawd6b">
                <SparklinesLine color={trendColor} style={{
              strokeWidth: 1.8,
              fill: 'none'
            }} data-id="element-1242" data-mp="14cvzvpxiotgq" />
              </Sparklines>
            </Box>
          </Box>}
      </Box>
    </Card>;
};
export default KPICard;